import { useMutation } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { gql } from '@/__generated__/index';
import { useMe } from '@/routes/_app/use-me';

const SET_OPEN_AI_KEY = gql(`
  mutation SetOpenAIKey($enabled: Boolean!, $key: String!) {
    setOpenAIKey(enabled: $enabled, key: $key) {
      enabled
      key
      createdAt
      availableModels
    }
  }
`);

type SetOpenAIKeyVariables = {
  enabled: boolean;
  key: string;
};

export const useSetOpenAiMutation = () => {
  const toast = useToast();
  const me = useMe();

  const [setOpenAIKeyMutation, { loading }] = useMutation(SET_OPEN_AI_KEY, {
    onError(error) {
      let subTitle = error.message;

      if (error.message.includes('html')) {
        subTitle = 'Please try again later';
      }

      if (error.message.includes('401')) {
        subTitle = 'Invalid API key';
      }

      toast.error({
        title: 'Failed to set OpenAI api key',
        subTitle,
      });
    },
    onCompleted() {
      toast.info({
        title: 'OpenAI api key was set successfully',
      });
    },
    update(cache, { data }) {
      const openAIKey = data?.setOpenAIKey;
      if (openAIKey) {
        cache.modify({
          id: cache.identify({ __typename: 'User', id: me.id }),
          fields: {
            protectedFields(data = {}) {
              return {
                ...data,
                openAIKey,
              };
            },
          },
        });
      }
    },
  });

  return {
    setOpenAIKeyMutation,
    loading,
  };
};

export const useSetOpenAIKey = () => {
  const { setOpenAIKeyMutation, loading } = useSetOpenAiMutation();

  const setOpenAIKey = ({ key, enabled = true }: SetOpenAIKeyVariables) => {
    return setOpenAIKeyMutation({
      variables: { key, enabled },
    });
  };

  return {
    setOpenAIKey,
    loading,
  } as const;
};

export const useSwitchOpenAIKey = () => {
  const toast = useToast();

  const { setOpenAIKeyMutation, loading } = useSetOpenAiMutation();

  const switchOpenAIKey = ({ key, enabled = true }: SetOpenAIKeyVariables) => {
    return setOpenAIKeyMutation({
      variables: { key, enabled },
      onCompleted: (data) => {
        toast.info({
          title: `OpenAI api key was ${data.setOpenAIKey.enabled ? 'unpaused' : 'paused'} successfully`,
        });
      },
      onError: (error) => {
        toast.error({
          title: 'Failed to pause OpenAI api key',
          subTitle: error.message,
        });
      },
    });
  };

  return {
    switchOpenAIKey,
    loading,
  } as const;
};
